// Table-discount derivation for the POS V2 table page. Pure functions (no
// store access) so the chip/modal logic is unit-testable like
// table-session-derivation; the caller feeds the table's orders.
import { DiscountType } from '@/enums/DiscountType';
import { IDiscount } from '@/interfaces/IDiscount';
import { IOrder } from '@/interfaces/IOrder';
import { roundPrice } from '@/utils';
import { isPendingOrder } from '@/utils/orders.utils';

/** Marker the API writes into metadata.source on table-discount rows. */
export const TABLE_DISCOUNT_SOURCE = 'table';

export interface ITableDiscountState {
  /** Freshest table discount on the table, or null when none is applied. */
  active: { isPercentage: boolean; value: number } | null;
  /** Eligible pending orders WITHOUT a table row (only when active). */
  uncoveredEligibleCount: number;
  hasPosOwnedOrders: boolean;
  /** Any pending order with money already collected. */
  paidMoneyPresent: boolean;
}

export function isTableDiscountRow(discount: IDiscount): boolean {
  return (
    discount.type === DiscountType.Manual &&
    discount.metadata?.source === TABLE_DISCOUNT_SOURCE
  );
}

function tableRowsOf(order: IOrder): IDiscount[] {
  return (order.discounts ?? []).filter(isTableDiscountRow);
}

/**
 * The server only ever touches `WHERE table_session_id = <session>`, so
 * session-less legacy orders and POS-owned orders must not count as
 * coverable.
 */
function isEligible(order: IOrder, openSessionId: string | null): boolean {
  return (
    order.posDeviceId == null &&
    openSessionId != null &&
    order.tableSession?.id === openSessionId
  );
}

function rowStamp(row: IDiscount): number {
  const ms = new Date(row.updatedAt).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Detection intentionally matches on metadata.source === 'table' regardless
 * of the row's tableSessionId: a discounted order moved onto another
 * occupied table makes the destination chip show as active with an
 * uncovered count — accepted; staff re-apply/remove self-heals.
 */
export function deriveTableDiscountState(
  orders: IOrder[],
  openSessionId: string | null
): ITableDiscountState {
  const pending = orders.filter(isPendingOrder);

  // Freshest table row wins (rows can diverge after partial re-applies or
  // moves — same freshest-wins rationale as deriveOpenTableSession).
  let bestRow: IDiscount | null = null;
  let bestStamp = -Infinity;
  for (const order of pending) {
    for (const row of tableRowsOf(order)) {
      const stamp = rowStamp(row);
      if (stamp >= bestStamp) {
        bestRow = row;
        bestStamp = stamp;
      }
    }
  }

  const active = bestRow
    ? {
        isPercentage:
          typeof bestRow.metadata?.tableIsPercentage === 'boolean'
            ? bestRow.metadata.tableIsPercentage
            : bestRow.isPercentage,
        value:
          typeof bestRow.metadata?.tableValue === 'number'
            ? bestRow.metadata.tableValue
            : bestRow.value,
      }
    : null;

  const uncoveredEligibleCount = active
    ? pending.filter(
        (order) =>
          isEligible(order, openSessionId) && tableRowsOf(order).length === 0
      ).length
    : 0;

  return {
    active,
    uncoveredEligibleCount,
    hasPosOwnedOrders: pending.some((order) => order.posDeviceId != null),
    paidMoneyPresent: pending.some((order) => order.totalPaid > 0),
  };
}

/**
 * Delta-based preview of the table total after applying `next` (the server
 * stays authoritative; restaurant round-mode drift is acceptable): undo the
 * current table shares, then apply the new one clamped to each order's
 * remaining discountable room.
 */
export function computeTableDiscountPreview(
  orders: IOrder[],
  openSessionId: string | null,
  next: { isPercentage: boolean; value: number }
): { from: number; to: number } {
  const pending = orders.filter(isPendingOrder);
  const from = roundPrice(pending.reduce((sum, order) => sum + order.total, 0));

  const eligible = pending.filter((order) => isEligible(order, openSessionId));

  let currentShareSum = 0;
  let roomSum = 0;
  let percentShareSum = 0;

  for (const order of eligible) {
    const currentShare = roundPrice(
      tableRowsOf(order).reduce(
        (sum, row) =>
          sum +
          (row.isPercentage
            ? roundPrice((order.subtotal * row.value) / 100)
            : row.value),
        0
      )
    );
    const other = roundPrice((order.discountedAmount ?? 0) - currentShare);
    const baseRoom = Math.max(
      0,
      roundPrice(order.subtotal + (order.deliveryCost ?? 0) - other)
    );
    // Mirror the server's unpaid-remainder cap (computeOrderRoom): a table
    // discount never drops an order's total below what was already collected,
    // so the preview must not either.
    const room = Math.max(0, roundPrice(baseRoom - (order.totalPaid || 0)));

    currentShareSum = roundPrice(currentShareSum + currentShare);
    roomSum = roundPrice(roomSum + room);
    if (next.isPercentage) {
      percentShareSum = roundPrice(
        percentShareSum +
          Math.min(roundPrice((order.subtotal * next.value) / 100), room)
      );
    }
  }

  const newTotalShare = next.isPercentage
    ? percentShareSum
    : Math.min(next.value, roomSum);

  const to = roundPrice(from + currentShareSum - newTotalShare);
  return { from, to };
}
